import { useEffect, useRef } from 'react';
import {
  useEditor,
  EditorContent,
  type Editor as TiptapEditor,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import {
  Bold,
  Italic,
  Strikethrough,
  Underline as UnderlineIcon,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link2,
  Link2Off,
  Minus,
  Undo,
  Redo,
  Image as ImageIcon,
  Upload,
} from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * 이미지 파일 업로드 핸들러. 지정 시 서버로 업로드하고 응답 URL을 <img src>로 사용.
   * 미지정 시 Base64 dataURL로 인라인 (로컬 미리보기 용도).
   */
  onUploadImage?: (file: File) => Promise<{ url: string }>;
}

/**
 * TipTap 기반 리치 텍스트 에디터.
 * - HTML 문자열을 입출력합니다. 저장 시 sanitize는 호출 측에서 처리.
 * - 저장된 값을 다시 보여줄 때는 EditorContent의 자체 렌더링을 사용하거나
 *   bottom-side에서 DOMPurify로 sanitize 후 `dangerouslySetInnerHTML`로 표시하세요.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled,
  onUploadImage,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Placeholder.configure({
        placeholder: placeholder ?? '내용을 입력하세요',
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: { class: 'announcement-rich-image' },
      }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: {
        class:
          'announcement-rich-content min-h-[220px] px-4 py-3 focus:outline-none',
      },
    },
  });

  /** 외부에서 value가 비동기로 들어오는 경우(편집 모드 초기 로드) 반영 */
  useEffect(() => {
    if (!editor) return;
    if (value === editor.getHTML()) return;
    if (value === '' && editor.getText() === '') return;
    editor.commands.setContent(value || '', { emitUpdate: false });
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) {
    return (
      <div className="min-h-[260px] rounded-lg border border-gray-300 bg-gray-50" />
    );
  }

  return (
    <div
      className={`rounded-lg border border-gray-300 bg-white focus-within:border-primary-400 focus-within:ring-1 focus-within:ring-primary-200 ${
        disabled ? 'opacity-60' : ''
      }`}
    >
      <Toolbar editor={editor} disabled={disabled} onUploadImage={onUploadImage} />
      <EditorContent editor={editor} />
    </div>
  );
}

interface ToolbarProps {
  editor: TiptapEditor;
  disabled?: boolean;
  onUploadImage?: (file: File) => Promise<{ url: string }>;
}

/** 업로드 가능한 이미지 MIME 화이트리스트 */
const ALLOWED_IMAGE_MIME = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

/** 업로드 가능한 최대 파일 크기 (5MB)
 *  - 더미 단계에서는 localStorage에 base64로 저장되므로 보수적으로 제한
 *  - 백엔드 업로드 API 도입 후에는 상향 가능 */
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('파일을 읽지 못했습니다.'));
    reader.readAsDataURL(file);
  });
}

function Toolbar({ editor, disabled, onUploadImage }: ToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('링크 URL을 입력하세요', prev ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url })
      .run();
  };

  const handleImageUrl = () => {
    const url = window.prompt('이미지 URL을 입력하세요', 'https://');
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const handleImageFile = async (file: File) => {
    if (!ALLOWED_IMAGE_MIME.includes(file.type)) {
      alert('지원하지 않는 이미지 형식입니다. (png, jpg, gif, webp, svg)');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      alert('이미지 크기는 최대 5MB까지 업로드할 수 있습니다.');
      return;
    }
    try {
      let src: string;
      if (onUploadImage) {
        const result = await onUploadImage(file);
        src = result.url;
      } else {
        // 업로더 미지정 시 fallback: Base64 dataURL 인라인
        src = await readFileAsDataUrl(file);
      }
      editor.chain().focus().setImage({ src, alt: file.name }).run();
    } catch {
      alert('이미지를 첨부하지 못했습니다.');
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleImageFile(file);
    e.target.value = ''; // 동일 파일 재선택 가능하도록 초기화
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
      <ToolbarButton
        label="굵게"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={disabled}
      >
        <Bold size={15} />
      </ToolbarButton>
      <ToolbarButton
        label="기울임"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={disabled}
      >
        <Italic size={15} />
      </ToolbarButton>
      <ToolbarButton
        label="밑줄"
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        disabled={disabled}
      >
        <UnderlineIcon size={15} />
      </ToolbarButton>
      <ToolbarButton
        label="취소선"
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={disabled}
      >
        <Strikethrough size={15} />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label="제목 2"
        active={editor.isActive('heading', { level: 2 })}
        onClick={() =>
          editor.chain().focus().toggleHeading({ level: 2 }).run()
        }
        disabled={disabled}
      >
        <Heading2 size={15} />
      </ToolbarButton>
      <ToolbarButton
        label="제목 3"
        active={editor.isActive('heading', { level: 3 })}
        onClick={() =>
          editor.chain().focus().toggleHeading({ level: 3 }).run()
        }
        disabled={disabled}
      >
        <Heading3 size={15} />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label="글머리 기호"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        disabled={disabled}
      >
        <List size={15} />
      </ToolbarButton>
      <ToolbarButton
        label="번호 매기기"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={disabled}
      >
        <ListOrdered size={15} />
      </ToolbarButton>
      <ToolbarButton
        label="인용"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        disabled={disabled}
      >
        <Quote size={15} />
      </ToolbarButton>
      <ToolbarButton
        label="구분선"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        disabled={disabled}
      >
        <Minus size={15} />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label="링크"
        active={editor.isActive('link')}
        onClick={handleLink}
        disabled={disabled}
      >
        <Link2 size={15} />
      </ToolbarButton>
      <ToolbarButton
        label="링크 제거"
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={disabled || !editor.isActive('link')}
      >
        <Link2Off size={15} />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label="이미지 URL 삽입"
        onClick={handleImageUrl}
        disabled={disabled}
      >
        <ImageIcon size={15} />
      </ToolbarButton>
      <ToolbarButton
        label="이미지 파일 업로드"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
      >
        <Upload size={15} />
      </ToolbarButton>
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_IMAGE_MIME.join(',')}
        className="hidden"
        onChange={onFileChange}
      />

      <Divider />

      <ToolbarButton
        label="실행 취소"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={disabled || !editor.can().undo()}
      >
        <Undo size={15} />
      </ToolbarButton>
      <ToolbarButton
        label="다시 실행"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={disabled || !editor.can().redo()}
      >
        <Redo size={15} />
      </ToolbarButton>
    </div>
  );
}

interface ToolbarButtonProps {
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}
function ToolbarButton({
  label,
  onClick,
  active,
  disabled,
  children,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-7 w-7 items-center justify-center rounded text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40 ${
        active ? 'bg-primary-100 text-primary-700' : ''
      }`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-1 h-5 w-px bg-gray-300" />;
}
